import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import InboxOutlinedIcon from '@material-ui/icons/InboxOutlined';
import moment from 'moment';

import OverflowTooltip from '../../UI/OverflowTooltip/OverflowTooltip';
import Spinner from '../../UI/Spinner/Spinner';
import { estadosComprobante, EstadoComprobanteChip } from '../EstadoComprobanteTag/EstadoComprobanteTag';
import { claveComprobante } from '../hooks/useDatosComprobante';
import { nombreComprobante, texto } from '../textosComprobante';
import { groupBy } from '../../../assets/shared/utility';
import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import colores from './coloresBandeja';

const useStyles = makeStyles((theme) => ({
  lista: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    overflowY: 'auto',
    minHeight: 0
  },
  dia: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 16px',
    backgroundColor: '#f6f6f6',
    borderBottom: `1px solid ${colores.linea}`,
    fontSize: '11px',
    fontWeight: 'bold',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: colores.secundario
  },
  item: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '2px 12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${colores.linea}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#fafafa'
    },
    [theme.breakpoints.down('xs')]: {
      padding: '12px'
    }
  },
  seleccionado: {
    backgroundColor: `${colores.primarioClaro} !important`,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '4px',
      background: colores.gradiente
    }
  },
  cliente: {
    fontWeight: 500
  },
  sub: {
    fontSize: '12px',
    color: colores.tenue
  },
  total: {
    textAlign: 'right',
    fontWeight: 500,
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },
  estado: {
    textAlign: 'right'
  },
  anulado: {
    textDecoration: 'line-through',
    color: colores.tenue
  },
  vacio: {
    padding: '40px',
    textAlign: 'center',
    color: colores.tenue
  },
  ayuda: {
    padding: '10px 16px',
    fontSize: '11px',
    color: colores.tenue,
    textAlign: 'center',
    // Sin teclado (pantallas táctiles) los atajos no sirven
    '@media (hover: none)': {
      display: 'none'
    },
    '& kbd': {
      border: `1px solid ${colores.borde}`,
      borderBottomWidth: '2px',
      borderRadius: '3px',
      padding: '0 5px',
      fontSize: '10px',
      backgroundColor: '#fafafa'
    }
  }
}));

/** "Hoy", "Ayer" o "Lunes 26 oct." */
const nombreDia = (fecha) => {
  const dias = moment().startOf('day').diff(moment(fecha).startOf('day'), 'days');
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Ayer';
  const nombre = moment(fecha).format('dddd D MMM');
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
};

const escribiendo = () => ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement && document.activeElement.tagName);

/**
 * Lista de comprobantes agrupada por día, con el estado de cada uno. Se recorre con ↑/↓ y M abre el mail
 * (no mientras se escribe en un campo).
 */
const BandejaLista = ({ comprobantes, loading, seleccionado, onSeleccionar, onMail }) => {
  const classes = useStyles();
  const lista = useRef(null);

  // Los handlers del teclado leen siempre lo último sin volver a registrar el listener.
  const actual = useRef();
  actual.current = { comprobantes, seleccionado, onSeleccionar, onMail };

  useEffect(() => {
    const alPresionar = (event) => {
      if (escribiendo() || event.ctrlKey || event.altKey || event.metaKey) return;
      const { comprobantes: items, seleccionado: clave, onSeleccionar: seleccionar, onMail: mail } = actual.current;
      const indice = items.findIndex((comprobante) => claveComprobante(comprobante) === clave);

      if (event.key === 'ArrowDown' && indice < items.length - 1) {
        event.preventDefault();
        seleccionar(items[indice + 1]);
      } else if (event.key === 'ArrowUp' && indice > 0) {
        event.preventDefault();
        seleccionar(items[indice - 1]);
      } else if (event.key.toLowerCase() === 'm' && indice >= 0) {
        event.preventDefault();
        mail();
      }
    };
    document.addEventListener('keydown', alPresionar);
    return () => document.removeEventListener('keydown', alPresionar);
  }, []);

  // El seleccionado siempre a la vista (al moverse con el teclado).
  useEffect(() => {
    const elemento = lista.current && lista.current.querySelector(`[data-clave="${seleccionado}"]`);
    if (elemento) elemento.scrollIntoView({ block: 'nearest' });
  }, [seleccionado]);

  if (loading) {
    return (
      <div className={classes.lista}>
        <Spinner size="small" />
      </div>
    );
  }

  if (comprobantes.length === 0) {
    return (
      <div className={`${classes.lista} ${classes.vacio}`}>
        <InboxOutlinedIcon style={{ fontSize: 40 }} />
        <Typography variant="body2">No hay comprobantes con esos filtros</Typography>
      </div>
    );
  }

  const porDia = groupBy(comprobantes, (comprobante) => moment(comprobante.fecha).format('YYYY-MM-DD'));

  return (
    <div className={classes.lista} ref={lista}>
      {Object.keys(porDia).map((dia) => (
        <React.Fragment key={dia}>
          <div className={classes.dia}>
            <span>{nombreDia(dia)}</span>
            <span>{porDia[dia].length}</span>
          </div>
          {porDia[dia].map((comprobante) => {
            const clave = claveComprobante(comprobante);
            const anulado = comprobante.estado === estadosComprobante.Anulado.nombre;
            const subtitulo = `${nombreComprobante(comprobante)} · ${moment(comprobante.fecha).format('HH:mm')}`;
            return (
              <div
                key={clave}
                data-clave={clave}
                className={`${classes.item} ${clave === seleccionado ? classes.seleccionado : ''}`}
                onClick={() => onSeleccionar(comprobante)}
              >
                <OverflowTooltip className={`${classes.cliente} ${anulado ? classes.anulado : ''}`}>
                  {`${texto(comprobante.idCliente)} - ${texto(comprobante.nombre)}`}
                </OverflowTooltip>
                <div className={`${classes.total} ${anulado ? classes.anulado : ''}`}>{formatNumberWithMonedaSigno(comprobante.total)}</div>
                <OverflowTooltip className={classes.sub}>{subtitulo}</OverflowTooltip>
                <div className={classes.estado}>
                  <EstadoComprobanteChip estado={comprobante.estado} style={{ height: '20px' }} />
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
      <div className={classes.ayuda}>
        Usá <kbd>↑</kbd> <kbd>↓</kbd> para moverte y <kbd>M</kbd> para enviar por mail
      </div>
    </div>
  );
};

export default BandejaLista;
